import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useAnalytics } from "@/hooks/useAnalytics";
import { suggestionsApi, type Followup, type Recommendation } from "@/api/suggestions";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const formatFollowupDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInHours < 48) return 'Yesterday';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

type NotificationItem = {
  id: number;
  type: 'nudge' | 'recommendation';
  suggestion: number;
  suggestion_title: string;
  title: string;
  content: string;
  created_at: string;
  is_read?: boolean;
  followup_type?: string;
  recommendation_type?: string;
};

export const NotificationButton = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { trackUserInteraction } = useAnalytics();

  // Fetch followups (nudges) with infinite query
  const {
    data: followupsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchFollowups
  } = useInfiniteQuery({
    queryKey: ['followups'],
    queryFn: ({ pageParam = 1 }) =>
      suggestionsApi.getAllFollowups({
        ordering: '-created_at',
        page_size: 10,
        page: pageParam
      }),
    getNextPageParam: (lastPage, allPages) => {
      // Check if there are more pages
      if (!lastPage || !allPages) return undefined;
      const totalCount = lastPage.count;
      const loadedCount = allPages.reduce((acc, page) => acc + page.results.length, 0);
      return loadedCount < totalCount ? allPages.length + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: isAuthenticated,
    refetchInterval: 60000, // Refetch every minute
  });

  // Fetch recommendations with infinite query
  const {
    data: recommendationsData,
    fetchNextPage: fetchNextRecommendationsPage,
    hasNextPage: hasNextRecommendationsPage,
    isFetchingNextPage: isFetchingNextRecommendationsPage,
  } = useInfiniteQuery({
    queryKey: ['notifications-recommendations'],
    queryFn: ({ pageParam = 1 }) =>
      suggestionsApi.getAllRecommendations({
        ordering: '-created_at',
        page_size: 10,
        page: pageParam
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || !allPages) return undefined;
      const totalCount = lastPage.count;
      const loadedCount = allPages.reduce((acc, page) => acc + page.results.length, 0);
      return loadedCount < totalCount ? allPages.length + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: isAuthenticated,
    refetchInterval: 60000,
  });

  const followups = followupsData?.pages.flatMap(page => page.results) || [];
  const recommendations = recommendationsData?.pages.flatMap(page => page.results) || [];

  // Combine and sort all notifications by date
  const allNotifications: NotificationItem[] = [
    ...followups.map(f => ({
      id: f.id,
      type: 'nudge' as const,
      suggestion: f.suggestion,
      suggestion_title: f.suggestion_title,
      title: f.followup_type.replace('_', ' '),
      content: f.content,
      created_at: f.created_at,
      is_read: f.is_read,
      followup_type: f.followup_type,
    })),
    ...recommendations.map(r => ({
      id: r.id,
      type: 'recommendation' as const,
      suggestion: r.suggestion,
      suggestion_title: r.suggestion_title || '',
      title: r.title,
      content: r.description,
      created_at: r.created_at,
      is_read: r.is_read,
      recommendation_type: r.recommendation_type,
    }))
  ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const unreadFollowups = followups.filter(f => !f.is_read);
  const unreadRecommendations = recommendations.filter(r => !r.is_read);
  const notificationCount = unreadFollowups.length + unreadRecommendations.length;

  const handleNotificationClick = async (notification: NotificationItem) => {
    trackUserInteraction(notification.type === 'nudge' ? 'view_nudge' : 'view_recommendation', 'notification_bell');
    navigate(`/suggestions/${notification.suggestion}`);
  };

  return (
    <DropdownMenu onOpenChange={(open) => {
      if (open) {
        // Refetch when dropdown opens to get latest read status
        refetchFollowups();
      }
    }}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative p-3 h-auto w-auto rounded-full bg-gray-50 border border-gray-200 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0">
          <Bell size={40} />
          {notificationCount > 0 && (
            <span className="absolute top-0 right-0 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-medium">
              {notificationCount > 9 ? '9+' : notificationCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Notifications</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {allNotifications.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-500">
            No notifications at the moment
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {allNotifications.map((notification) => (
              <DropdownMenuItem
                key={`${notification.type}-${notification.id}`}
                className="cursor-pointer p-4 focus:bg-gray-50"
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-2 w-full">
                  <div className="w-2 h-2 mt-1.5 flex-shrink-0">
                    {!notification.is_read && (
                      <div className="w-2 h-2 rounded-full bg-blue-600" />
                    )}
                  </div>
                  <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-medium capitalize ${
                        notification.type === 'nudge' ? 'text-purple-600' : 'text-blue-600'
                      }`}>
                        {notification.type === 'nudge' ? notification.title : 'Recommendation'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatFollowupDate(notification.created_at)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-1">
                      {notification.suggestion_title}
                    </p>
                    {notification.type === 'recommendation' ? (
                      <p className="text-sm text-gray-900 line-clamp-2">
                        {notification.title}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-900 line-clamp-2">
                        {notification.content}
                      </p>
                    )}
                  </div>
                </div>
              </DropdownMenuItem>
            ))}
          </div>
        )}
        {(hasNextPage || hasNextRecommendationsPage) && (
          <div className="p-2 border-t">
            <Button
              onClick={() => {
                if (hasNextPage) fetchNextPage();
                if (hasNextRecommendationsPage) fetchNextRecommendationsPage();
              }}
              disabled={isFetchingNextPage || isFetchingNextRecommendationsPage}
              variant="ghost"
              className="w-full text-sm"
            >
              {(isFetchingNextPage || isFetchingNextRecommendationsPage) ? 'Loading...' : 'Load More'}
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
